import {
  ApplicationConfig,
  provideAppInitializer,
  provideZoneChangeDetection,
  inject,
  isDevMode,
} from '@angular/core';
import { provideRouter, withNavigationErrorHandler, withRouterConfig } from '@angular/router';
import { routes } from './app.routes';
import { provideGraphQL } from './graphql.providers';
import {
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnalytics } from './analytics/provide-analytics';
import { AppUpdateService } from './updates/app-update.service';
import { chunkReloadNavigationError } from './updates/chunk-reload';
import { provideAppTransloco } from './i18n/transloco.providers';
import { DEFAULT_COOKIE_POLICY_URL } from '@ricohproducts/consent';
import { provideConsent, provideConsentLanguageSync } from '@ricohproducts/consent/angular';
import { TranslocoService } from '@jsverse/transloco';

function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.OUTLOOK_CLIENT_ID,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

function MSALGuardConfigFactory() {
  return { interactionType: InteractionType.Popup };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      // A lazy route whose chunk a deploy deleted reloads onto the new build.
      withNavigationErrorHandler(chunkReloadNavigationError),
      // Pinned to today's default: Angular 22 switches it to 'always'.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
    ),
    provideAppInitializer(() => inject(AppUpdateService).start()),
    // Runtime i18n (Transloco): loads /i18n/<lang>.json for the browser's
    // language. Before the consent providers so the banner starts in it.
    // Mirrors workplace-settings.
    ...provideAppTransloco(),
    // The shared cookie consent banner, in the package's own copy and the
    // app's language. Behind CONSENT_ENABLED: off, it never starts.
    provideConsent({
      startOn: environment.CONSENT_ENABLED ? 'init' : 'manual',
      policies: {
        cookies: environment.COOKIE_POLICY_URL || DEFAULT_COOKIE_POLICY_URL,
        privacy: environment.PRIVACY_POLICY_URL || undefined,
      },
    }),
    provideConsentLanguageSync(TranslocoService),
    ...provideAnalytics(),
    ...provideGraphQL(),
    MsalService,
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
